import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { styled } from "@mui/material";
import { amber, green } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import SnackbarContent from "@mui/material/SnackbarContent";
import PropTypes from "prop-types";
import React from "react";

const variantIcon = {
    success: CheckCircleIcon,
    warning: WarningIcon,
    error: ErrorIcon,
    info: InfoIcon,
};

const StyledSnackbarContent = styled(SnackbarContent)(({ theme, variant }) => ({
    ...(variant === "success" && {
        backgroundColor: green[600],
    }),
    ...(variant === "error" && {
        backgroundColor: theme.palette.error.dark,
    }),
    ...(variant === "info" && {
        backgroundColor: theme.palette.primary.dark,
    }),
    ...(variant === "warning" && {
        backgroundColor: amber[700],
    }),
}));

const StyledIcon = styled("span")(({ theme }) => ({
    fontSize: 20,
    opacity: 0.9,
    marginRight: theme.spacing(1),
    display: "flex",
    alignItems: "center",
}));

const StyledMessage = styled("span")({
    display: "flex",
    alignItems: "center",
    whiteSpace: "pre-line",
});

const MySnackbarContent = React.forwardRef(function MySnackbarContent(props, ref) {
    const { className, message, onClose, variant, ...other } = props;
    const Icon = variantIcon[variant];

    return (
        <StyledSnackbarContent
            ref={ref}
            variant={variant}
            className={className}
            aria-describedby="client-snackbar"
            message={
                <StyledMessage id="client-snackbar">
                    <StyledIcon>
                        <Icon />
                    </StyledIcon>
                    {message}
                </StyledMessage>
            }
            action={[
                <IconButton key="close" aria-label="Close" color="inherit" onClick={onClose}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>,
            ]}
            {...other}
        />
    );
});

MySnackbarContent.propTypes = {
    className: PropTypes.string,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onClose: PropTypes.func,
    variant: PropTypes.oneOf(["success", "warning", "error", "info"]).isRequired,
};

const MySnackbarContentWrapper = MySnackbarContent;

const CustomizedSnackbars = (props) => {
    const { open, onClose, message, variant, vertical, horizontal, autoHideDuration } = props;
    return (
        <Snackbar
            anchorOrigin={{
                vertical: vertical,
                horizontal: horizontal,
            }}
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={onClose}
        >
            <MySnackbarContentWrapper onClose={onClose} variant={variant} message={message} />
        </Snackbar>
    );
};

CustomizedSnackbars.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    variant: PropTypes.oneOf(["success", "warning", "error", "info"]).isRequired,
    vertical: PropTypes.string,
    horizontal: PropTypes.string,
    autoHideDuration: PropTypes.number,
};

export default CustomizedSnackbars;
